import React, { useMemo, useState } from "react";

import { Button, ButtonText } from "../../ui/button";
import { Text } from "../../ui/text";
import { VStack } from "../../ui/vstack";
import BreakdownSkeleton from "./BreakdownSkeleton";
import Line from "./Line";
import { visibleLines, withGroupCaptions } from "./layout";
import { PaymentBreakdownProps } from "./types";

/**
 * Ordered list of payment lines returned by the backend. Shared across the
 * center brand checkout, the affiliate payout checkout, work order and
 * invoice views, and the mobile creator's "what you'll receive" and wallet
 * summaries — so it only assumes lines arrive pre-ordered and pre-labeled by
 * the API, nothing feature-specific.
 */
const PaymentBreakdown = React.memo(
  ({
    lines,
    density = "full",
    loading = false,
    groups = [],
  }: PaymentBreakdownProps) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const entries = useMemo(
      () => withGroupCaptions(visibleLines(lines), groups),
      [lines, groups],
    );

    if (loading) return <BreakdownSkeleton />;

    const isCompact = density === "compact" && !isExpanded;

    if (isCompact) {
      const totalEntries = entries.filter(
        (entry) => entry.type === "line" && entry.line.role === "TOTAL",
      );
      return (
        <VStack space="sm">
          {totalEntries.map((entry, index) =>
            entry.type === "line" ? (
              <Line key={`${entry.line.kind}-${index}`} line={entry.line} />
            ) : null,
          )}
          <Button
            variant="link"
            action="brand"
            size="sm"
            onPress={() => setIsExpanded(true)}
          >
            <ButtonText>Ver detalle</ButtonText>
          </Button>
        </VStack>
      );
    }

    return (
      <VStack space="sm">
        {entries.map((entry, index) =>
          entry.type === "caption" ? (
            <Text
              key={`caption-${entry.label}-${index}`}
              size="2xs"
              bold
              className="uppercase text-typography-500 tracking-wide"
            >
              {entry.label}
            </Text>
          ) : (
            <Line key={`${entry.line.kind}-${index}`} line={entry.line} />
          ),
        )}
      </VStack>
    );
  },
);

PaymentBreakdown.displayName = "PaymentBreakdown";

export default PaymentBreakdown;
